<template>
  <div class="space-y-4">
    <!-- Draft Orders List -->
    <div class="space-y-3">
      <div
        v-for="draft in draftOrders"
        :key="draft.store_slug"
        class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 overflow-hidden rounded-lg"
      >
        <!-- Store Image Placeholder -->
        <div class="bg-surface-level-200 dark:bg-surface-level-700 h-32 relative">
          <div class="absolute flex inset-0 items-center justify-center">
            <svg
              class="h-12 text-content-level-400 w-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
        </div>

        <!-- Store Info -->
        <div class="p-4">
          <div>
            <h4 class="dark:text-content-inverse font-semibold text-base text-content-level-900">
              {{ draft.store_name }}
            </h4>

            <div
              class="dark:text-content-level-400 flex items-center mt-1 text-content-level-500 text-sm"
            >
              <svg
                class="h-4 mr-1 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />

                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>

              <span
                >{{ draft.address }}{{ draft.city ? `, ${draft.city}` : ''
                }}{{ draft.state ? `, ${draft.state}` : ''
                }}{{ draft.zip ? ` ${draft.zip}` : '' }}</span
              >
            </div>
          </div>

          <!-- Items Preview -->
          <div class="mt-4 space-y-1">
            <div
              v-for="item in draft.items.slice(0, 2)"
              :key="item.item_name"
              class="dark:text-content-level-400 text-content-level-600 text-sm"
            >
              {{
                $t('commerce.draftOrdersView.quantityXName', {
                  quantity: item.quantity,
                  name: item.item_name
                })
              }}
            </div>

            <div
              v-if="draft.items.length > 2"
              class="dark:text-content-level-500 text-content-level-500 text-sm"
            >
              {{ $t('commerce.draftOrdersView.countMoreItems', { count: draft.items.length - 2 }) }}
            </div>
          </div>

          <!-- Price and Item Count -->
          <div class="flex items-center justify-between mt-0">
            <div class="dark:text-content-level-400 text-content-level-500 text-sm">
              {{
                $t('commerce.draftOrdersView.itemCount', {
                  count: draft.item_count
                })
              }}
            </div>

            <div class="dark:text-content-inverse font-semibold text-base text-content-level-900">
              {{ formatCurrency(draft.subtotal) }}
            </div>
          </div>

          <!-- Actions -->
          <div class="flex mt-4 space-x-2">
            <button
              type="button"
              class="bg-surface-clear border-error btn btn-outline flex flex-1 text-error"
              @click="() => removeDraft(draft.store_slug)"
            >
              <svg
                class="h-4 mr-1 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                /></svg
              >{{ $t('commerce.draftOrdersView.remove') }}
            </button>

            <button
              type="button"
              class="btn btn-active btn-primary flex-2"
              @click="() => continueOrder(draft.store_slug)"
            >
              {{ $t('commerce.draftOrdersView.continueOrder') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="draftOrders.length === 0"
        class="py-8 text-center"
      >
        <div
          class="bg-surface-level-100 dark:bg-surface-level-800 flex h-16 items-center justify-center mb-4 mx-auto rounded-full w-16"
        >
          <ShoppingCartIcon class="h-8 text-content-level-400 w-8" />
        </div>

        <h4 class="dark:text-content-inverse font-medium mb-2 text-content-level-900 text-sm">
          {{ $t('commerce.draftOrdersView.yourCartIsEmpty') }}
        </h4>

        <p class="dark:text-content-level-400 mb-4 text-content-level-500 text-sm">
          {{ $t('commerce.draftOrdersView.addSomeDeliciousItemsToGetStarted') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { useDraftOrders } from '~/composables/useDraftOrders'
import { formatCurrency } from '~/utils/formatCurrency'

const { continueOrder, draftOrders, removeDraft } = useDraftOrders()
</script>
